import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TablePagination,
} from "@mui/material";

import {
  PeopleAlt,
  Business,
  LocationOn,
  CheckCircle,
  Cancel,
  PendingActions,
  Refresh,
  Groups,
  Schedule,
  TrendingUp,
  Apartment,
  Clear,
  Search,
} from "@mui/icons-material";

import { GetVisitorDashboard } from "../../services/attendance-change";

const VisitorDashboard = () => {
  const today = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(false);
const [selectedDepartment, setSelectedDepartment] = useState("");
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: today,
    toDate: today,
    department: "",
    visitingType: "",
    location: "",
    selectType: "",
    approval: "",
  });

  const [dashboard, setDashboard] = useState({
    summary: null,
    trend: [],
    departments: [],
    visitingTypes: [],
    approvals: [],
    approvalSummary: null,
    companies: [],
    hosts: [],
    locations: [],
    selectTypes: [],
    duration: null,
    durationCategories: [],
    details: [],
  });
  const [search, setSearch] = useState("");
useEffect(() => {
  setPage(0);
}, [
  search,
  filters.fromDate,
  filters.toDate,
  filters.department,
  filters.visitingType,
  filters.location,
  filters.selectType,
  filters.approval,
]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
const departmentVisitors = useMemo(() => {
  if (!selectedDepartment) return [];

  const visitorMap = {};

  dashboard.details
    .filter(
      (item) =>
        item.department?.trim().toLowerCase() ===
        selectedDepartment.trim().toLowerCase()
    )
    .forEach((item) => {
      const rawName = item.representing?.trim() || "Unknown Visitor";

      // Normalize name for grouping
      const key = rawName.toLowerCase();

      if (!visitorMap[key]) {
        visitorMap[key] = {
          visitorName: rawName,
          gatePasses: 0,
          visitors: 0,
        };
      }

      visitorMap[key].gatePasses += 1;

      visitorMap[key].visitors += Number(
        item.noOfPerson || 1
      );
    });

  return Object.values(visitorMap).sort(
    (a, b) => b.visitors - a.visitors
  );
}, [selectedDepartment, dashboard.details]);
  const loadDashboard = async (customFilters = filters) => {
    try {
      setLoading(true);

      const request = {
        data: {
          fromDate: customFilters.fromDate || null,
          toDate: customFilters.toDate || null,
          department: customFilters.department || null,
          visitingType: customFilters.visitingType || null,
          location: customFilters.location || null,
          selectType: customFilters.selectType || null,
          approval:
            customFilters.approval === ""
              ? null
              : Number(customFilters.approval),
        },
      };

      const response = await GetVisitorDashboard(request);

      const result = response?.data;

      setDashboard({
        summary: result?.summary || null,
        trend: result?.trend || [],
        departments: result?.departments || [],
        visitingTypes: result?.visitingTypes || [],
        approvals: result?.approvals || [],
        approvalSummary: result?.approvalSummary || null,
        companies: result?.companies || [],
        hosts: result?.hosts || [],
        locations: result?.locations || [],
        selectTypes: result?.selectTypes || [],
        duration: result?.duration || null,
        durationCategories: result?.durationCategories || [],
        details: result?.details || [],
      });
    } catch (error) {
      console.error("Visitor Dashboard Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    const resetFilters = {
      fromDate: today,
      toDate: today,
      department: "",
      visitingType: "",
      location: "",
      selectType: "",
      approval: "",
    };

    setFilters(resetFilters);
    loadDashboard(resetFilters);
  };

const filteredDetails = useMemo(() => {
  let details = dashboard.details || [];

  // ==============================
  // DATE + DEPARTMENT FILTER
  // ==============================
  details = details.filter((item) => {
    // ------------------------------
    // DATE FILTER
    // ------------------------------
    if (!item.createdDate) return false;

    const createdDate = String(item.createdDate).trim();

    // API format:
    // DD-MM-YYYY HH:mm:ss AM/PM
    const match = createdDate.match(
      /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)$/i
    );

    if (!match) return false;

    const [, day, month, year] = match;

    // Convert DD-MM-YYYY → YYYY-MM-DD
    const date = `${year}-${month}-${day}`;

    const fromDate = filters.fromDate || "1900-01-01";
    const toDate = filters.toDate || "9999-12-31";

    const dateMatch =
      date >= fromDate &&
      date <= toDate;

    if (!dateMatch) {
      return false;
    }

    // ------------------------------
    // DEPARTMENT FILTER
    // ------------------------------
    const selectedDepartment =
      filters.department?.trim().toLowerCase();

    // If department is not selected → show all departments
    if (!selectedDepartment) {
      return true;
    }

    const itemDepartment =
      item.department?.trim().toLowerCase();

    return itemDepartment === selectedDepartment;
  });

  // ==============================
  // SEARCH FILTER
  // ==============================
  if (!search.trim()) {
    return details;
  }

  const value = search.toLowerCase();

  return details.filter((item) =>
    [
      item.initiatorName,
      item.department,
      item.visitorName,
      item.visitingPersonName,
      item.personMeet,
      item.visitingType,
      item.purpose,
      item.representing,
      item.location,
      item.hostPersonToMeet,
      item.finalApprovalStatus,
    ]
      .filter(Boolean)
      .some((field) =>
        String(field)
          .toLowerCase()
          .includes(value)
      )
  );
}, [
  dashboard.details,
  filters.fromDate,
  filters.toDate,
  filters.department,
  search,
]);
  const summary = dashboard.summary || {};

  const StatCard = ({
    title,
    value,
    subtitle,
    icon,
    background,
  }) => (
    <Card
      sx={{
        height: "Max-content",
        
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
            >
              {title}
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ mt: 1 }}
            >
              {value ?? 0}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: background,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const SectionCard = ({
    title,
    subtitle,
    children,
    action,
  }) => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {action}
        </Box>

        {children}
      </CardContent>
    </Card>
  );

  const RankingList = ({
    data,
    labelField,
    title,
    icon,
    maxItems = 8,
  }) => {
    const items = data.slice(0, maxItems);

    const maxValue = Math.max(
      ...items.map((x) => Number(x.totalVisitors || 0)),
      1
    );

    return (
      <SectionCard title={title} action={icon}>
        {items.length === 0 ? (
          <Typography
            color="text.secondary"
            textAlign="center"
            py={3}
          >
            No data available
          </Typography>
        ) : (
          items.map((item, index) => {
            const value = Number(
              item.totalVisitors || 0
            );

            return (
              <Box key={index} mb={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  mb={0.6}
                >
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    noWrap
                    sx={{ maxWidth: "75%" }}
                  >
                    {item[labelField] || "Unknown"}
                  </Typography>

                  <Typography
                    fontSize={13}
                    fontWeight={700}
                  >
                    {item.gatePasses || 0}
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={(value / maxValue) * 100}
                  sx={{
                    height: 7,
                    borderRadius: 5,
                  }}
                />

               
              </Box>
            );
          })
        )}
      </SectionCard>
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";

    if (typeof date === "string") {
      return date.split(" ")[0];
    }

    return date;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";

      case "Denied":
        return "error";

      case "Pending":
        return "warning";

      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 2.5 }}>

      {/* ======================================================
          HEADER
      ====================================================== */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={800}
          >
            Visitor Dashboard
          </Typography>

          <Typography
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Visitor gatepass analytics & monitoring
          </Typography>
        </Box>

        <Tooltip title="Refresh Dashboard">
          <IconButton
            onClick={() => loadDashboard()}
            disabled={loading}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <Refresh />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ======================================================
          FILTERS
      ====================================================== */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Dashboard Filters
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Filter visitor data by date and category
              </Typography>
            </Box>

            <Button
              size="small"
              startIcon={<Clear />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Box>

          <Grid container spacing={2}>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                size="small"
                label="From Date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                size="small"
                label="To Date"
                name="toDate"
                value={filters.toDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
<Grid item xs={12} sm={6} md={2}>
  <Autocomplete
    fullWidth
    size="small"
    options={dashboard.departments || []}
    value={
      dashboard.departments?.find(
        (x) => x.department === filters.department
      ) || null
    }
    getOptionLabel={(option) =>
      option?.department || ""
    }
    isOptionEqualToValue={(option, value) =>
      option.department === value.department
    }
    onChange={(event, newValue) => {
      const department =
        newValue?.department || "";

      setFilters((prev) => ({
        ...prev,
        department,
      }));

      // Also update Department Visitor Analysis
      setSelectedDepartment(department);
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Department"
        placeholder="Search department..."
      />
    )}
  />
</Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Visiting Type"
                name="visitingType"
                value={filters.visitingType}
                onChange={handleChange}
                placeholder="All"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                select
                size="small"
                label="Approval"
                name="approval"
                value={filters.approval}
                onChange={handleChange}
              >
                <MenuItem value="">
                  All
                </MenuItem>

                <MenuItem value={1}>
                  Approved
                </MenuItem>

                <MenuItem value={2}>
                  Pending
                </MenuItem>

                <MenuItem value={8}>
                  Denied
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Box
                display="flex"
                justifyContent="flex-end"
              >
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={() => loadDashboard()}
                  disabled={loading}
                  sx={{
              
                    borderRadius: 2,
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                    />
                  ) : (
                    "Apply Filter"
                  )}
                </Button>
              </Box>
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <LinearProgress sx={{ mb: 2 }} />
      )}
    {/* ======================================================
          SELECT TYPE
      ====================================================== */}
{/* ======================================================
    APPROVAL STATUS + VISITING TYPES
====================================================== */}
<Grid container spacing={2} mb={3}>

  {/* ================= APPROVAL STATUS ================= */}
  <Grid item xs={12} md={6}>
    <Card
      sx={{
        height: "100%",
        minHeight: 100,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
        boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
      }}
    >
      <CardContent sx={{ p: 2 }}>

        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              Approval Status
            </Typography>

            <Typography
              sx={{
                fontSize: 25,
                fontWeight: 800,
                mt: 0.8,
                lineHeight: 1.1,
              }}
            >
              {dashboard.approvals.reduce(
                (sum, x) =>
                  sum + Number(x.gatePasses || 0),
                0
              )}
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                color: "text.secondary",
                mt: 0.6,
              }}
            >
              Total Visitor 
            </Typography>
          </Box>

          {/* Icon */}
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: "rgba(76,175,80,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircle
              sx={{
                fontSize: 22,
                color: "success.main",
              }}
            />
          </Box>
        </Box>

        {/* Approval Summary */}
        <Box
          display="flex"
          alignItems="center"
          gap={3}
          mt={1.5}
        >
          {dashboard.approvals.map((item, index) => {
            const isApproved =
              item.approvalStatus === "Approved";

            return (
              <Box key={index}>
                {/* Status */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  {isApproved ? (
                    <CheckCircle
                      sx={{
                        fontSize: 16,
                        color: "success.main",
                      }}
                    />
                  ) : (
                    <Cancel
                      sx={{
                        fontSize: 16,
                        color: "error.main",
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                    }}
                  >
                    {item.approvalStatus}
                  </Typography>
                </Box>

                {/* Visitor Count */}
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.secondary",
                    mt: 0.3,
                    ml: 2.3,
                  }}
                >
                  {item.totalVisitors || 0} Visitors
                </Typography>
              </Box>
            );
          })}
        </Box>

      </CardContent>
    </Card>
  </Grid>


  {/* ================= VISITING TYPES ================= */}
  <Grid item xs={12} md={6}>
    <RankingList
      title="Visiting Types"
      data={dashboard.visitingTypes}
      labelField="visitingType"
      icon={<PeopleAlt fontSize="small" />}
    />
  </Grid>

</Grid>

 
      
      

 
      {/* ======================================================
          DEPARTMENT / VISITING TYPE / LOCATION
      ====================================================== */}
      <Grid container spacing={2} mb={3}>

        <Grid item xs={12} md={4}>
          <RankingList
            title="Top Departments"
            data={dashboard.departments}
            labelField="department"
            icon={<Groups fontSize="small" />}
          />
        </Grid>

        {/* <Grid item xs={12} md={4}>
          <RankingList
            title="Visiting Types"
            data={dashboard.visitingTypes}
            labelField="visitingType"
            icon={<PeopleAlt fontSize="small" />}
          />
        </Grid> */}
         <Grid item xs={12} md={4}>
          <RankingList
            title="Top Companies"
            data={dashboard.companies}
            labelField="company"
            icon={<Business fontSize="small" />}
          />
        </Grid>

  <Grid item xs={12} md={4}>
          <RankingList
            title="Top Hosts"
            data={dashboard.hosts}
            labelField="hostPerson"
            icon={<PeopleAlt fontSize="small" />}
          />
        </Grid>

        

      </Grid>

      {/* ======================================================
          TREND
      ====================================================== */}
      {/* <Grid container spacing={2} mb={3}>

       <Grid item xs={12}>
  <SectionCard
    title="Visitor Trend"
    subtitle="Gatepasses and visitors by visit date"
    icon={<TrendingUp fontSize="small" />}
    action={
      <Box display="flex" gap={1}>
        <TextField
          type="date"
          size="small"
          label="From"
          value={filters.fromDate}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              fromDate: e.target.value,
            }))
          }
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            width: 145,
          }}
        />

        <TextField
          type="date"
          size="small"
          label="To"
          value={filters.toDate}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              toDate: e.target.value,
            }))
          }
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            width: 145,
          }}
        />

        <Button
          variant="contained"
          size="small"
          onClick={() => loadDashboard()}
          disabled={loading}
        >
          Apply
        </Button>
      </Box>
    }
  >
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        overflowX: "auto",
        overflowY: "hidden",
        pb: 1.5,
        pt: 1,
        minHeight: 150,
        height: 150,
        alignItems: "flex-end",

        "&::-webkit-scrollbar": {
          height: 6,
        },

        "&::-webkit-scrollbar-thumb": {
          borderRadius: 5,
          backgroundColor: "rgba(0,0,0,0.25)",
        },
      }}
    >
      {dashboard.trend.map((item, index) => {
        const max = Math.max(
          ...dashboard.trend.map(
            (x) => Number(x.totalVisitors || 0)
          ),
          1
        );

        const height =
          (Number(item.totalVisitors || 0) / max) * 90;

        return (
          <Tooltip
            key={index}
            title={`${formatDate(item.visitDate)} - ${
              item.totalVisitors
            } visitors / ${item.gatePasses} gatepasses`}
          >
            <Box
              sx={{
                minWidth: 28,
                width: 28,
                height: 125,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                cursor: "pointer",
              }}
            >
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  lineHeight: 1,
                  mb: 0.5,
                }}
              >
                {item.totalVisitors}
              </Typography>

              <Box
                sx={{
                  width: 14,
                  height: Math.max(height, 5),
                  borderRadius: "4px 4px 1px 1px",
                  bgcolor: "primary.main",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              />

              <Typography
                sx={{
                  fontSize: "8px",
                  mt: 0.8,
                  transform: "rotate(-45deg)",
                  whiteSpace: "nowrap",
                  transformOrigin: "top center",
                  color: "text.secondary",
                }}
              >
                {formatDate(item.visitDate)}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}

      {dashboard.trend.length === 0 && (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={100}
        >
          <Typography
            color="text.secondary"
            fontSize={14}
          >
            No visitor data available for selected dates
          </Typography>
        </Box>
      )}
    </Box>
  </SectionCard>
</Grid>
      </Grid> */}

      {/* ======================================================
          COMPANY / HOST / DURATION
      ====================================================== */}
      <Grid container spacing={2} mb={3}>

       <Grid item xs={12} md={4}>
          <RankingList
            title="Top Locations"
            data={dashboard.locations}
            labelField="visitLocation"
            icon={<LocationOn fontSize="small" />}
          />
        </Grid>

       {/* <Grid item xs={12} md={4}>
          <RankingList
            title="Visiting Types"
            data={dashboard.visitingTypes}
            labelField="visitingType"
            icon={<PeopleAlt fontSize="small" />}
          />
        </Grid> */}

        <Grid item xs={12} md={4}>
          <SectionCard
            title="Visit Duration"
            subtitle="Completed visitor duration"
            icon={<Schedule fontSize="small" />}
          >
            <Grid container spacing={2}>

              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Average
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                >
                  {dashboard.duration
                    ?.averageDurationHours || 0}
                  h
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Completed
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                >
                  {dashboard.duration
                    ?.completedVisits || 0}
                </Typography>
              </Grid>

            </Grid>

            <Divider sx={{ my: 2 }} />

            {dashboard.durationCategories.map(
              (item, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.8}
                >
                  <Typography fontSize={13}>
                    {item.durationCategory}
                  </Typography>

                  <Chip
                    size="small"
                    label={item.visitCount}
                  />
                </Box>
              )
            )}
          </SectionCard>
        </Grid>

      </Grid>

{/* ======================================================
    DEPARTMENT VISITOR ANALYSIS
====================================================== */}
<Grid item xs={12}>

  <Accordion
    defaultExpanded={false}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "12px !important",
      boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
      overflow: "hidden",

      "&:before": {
        display: "none",
      },
    }}
  >

    {/* ================= HEADER ================= */}
    <AccordionSummary
      sx={{
        px: 2.5,
        minHeight: 72,

        "&.Mui-expanded": {
          minHeight: 72,
        },

        "& .MuiAccordionSummary-content": {
          margin: "14px 0",
        },

        "& .MuiAccordionSummary-content.Mui-expanded": {
          margin: "14px 0",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        pr={1}
      >

        {/* Title */}
        <Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            Department Visitor Analysis
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              mt: 0.3,
            }}
          >
            Select a department to view its visitors
          </Typography>
        </Box>

        {/* Department Dropdown */}
       {/* Department Searchable Dropdown */}
<Autocomplete
  size="small"
  options={dashboard.departments || []}
  value={
    dashboard.departments?.find(
      (x) =>
        x.department?.trim().toLowerCase() ===
        selectedDepartment?.trim().toLowerCase()
    ) || null
  }
  getOptionLabel={(option) =>
    option?.department?.trim() || ""
  }
  isOptionEqualToValue={(option, value) =>
    option.department?.trim().toLowerCase() ===
    value.department?.trim().toLowerCase()
  }
  onChange={(event, newValue) => {
    setSelectedDepartment(
      newValue?.department?.trim() || ""
    );
  }}
  onClick={(e) => e.stopPropagation()}
  onFocus={(e) => e.stopPropagation()}
  sx={{
    width: 220,

    "& .MuiInputBase-root": {
      height: 40,
    },
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Department"
      placeholder="Search department..."
    />
  )}
/>

      </Box>
    </AccordionSummary>


    {/* ================= CONTENT ================= */}
    <AccordionDetails
      sx={{
        px: 2.5,
        pb: 2.5,
        pt: 0,
      }}
    >

      {!selectedDepartment ? (

        /* No Department */
        <Box
          sx={{
            height: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            color="text.secondary"
            fontSize={13}
          >
            Select a department to view visitors
          </Typography>
        </Box>

      ) : departmentVisitors.length === 0 ? (

        /* No Visitors */
        <Box
          sx={{
            height: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            color="text.secondary"
            fontSize={13}
          >
            No visitors found for{" "}
            <strong>{selectedDepartment}</strong>
          </Typography>
        </Box>

      ) : (

        /* Visitor List */
        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            pr: 1,

            "&::-webkit-scrollbar": {
              width: 6,
            },

            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                "rgba(0,0,0,0.2)",
              borderRadius: 5,
            },
          }}
        >

          {departmentVisitors.map(
            (visitor, index) => {

              const maxGatePasses =
                Math.max(
                  ...departmentVisitors.map(
                    (x) =>
                      Number(x.gatePasses || 0)
                  ),
                  1
                );

              const percentage =
                (Number(visitor.gatePasses || 0) /
                  maxGatePasses) *
                100;

              return (
                <Box
                  key={index}
                  sx={{
                    mb:
                      index ===
                      departmentVisitors.length - 1
                        ? 0
                        : 1.5,
                  }}
                >

                  {/* Visitor Name + Count */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={0.5}
                  >

                    <Typography
                      fontSize={13}
                      fontWeight={600}
                      noWrap
                      sx={{
                        maxWidth: "75%",
                      }}
                    >
                      {visitor.visitorName}
                    </Typography>

                    <Typography
                      fontSize={12}
                      fontWeight={700}
                    >
                      {visitor.gatePasses}
                    </Typography>

                  </Box>


                  {/* Progress */}
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      backgroundColor:
                        "rgba(0,0,0,0.07)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${percentage}%`,
                        height: "100%",
                        borderRadius: 5,
                        backgroundColor:
                          "primary.main",
                        transition:
                          "width 0.3s ease",
                      }}
                    />
                  </Box>


                  {/* Visitor Count */}
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: "text.secondary",
                      mt: 0.3,
                    }}
                  >
                    {visitor.visitors} visitor(s)
                  </Typography>

                </Box>
              );
            }
          )}

        </Box>
      )}

    </AccordionDetails>

  </Accordion>

</Grid>
      {/* ======================================================
          DETAILS
      ====================================================== */}
  <Card
  sx={{
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
  }}
>
  <CardContent sx={{ p: 2.5 }}>

    {/* =====================================================
        HEADER
    ===================================================== */}
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
      gap={2}
      flexWrap="wrap"
    >
      <Box>
        <Typography
          variant="h6"
          fontWeight={700}
        >
          Visitor Details
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
        >
          {filteredDetails.length} records
        </Typography>
      </Box>

      {/* SEARCH */}
      <TextField
        size="small"
        placeholder="Search visitor..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        sx={{
          width: 250,
        }}
        InputProps={{
          startAdornment: (
            <Search
              fontSize="small"
              sx={{
                mr: 1,
                color: "text.secondary",
              }}
            />
          ),
        }}
      />
    </Box>

    {/* =====================================================
        TABLE
    ===================================================== */}
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        maxHeight: 550,
        overflow: "auto",
      }}
    >
      <Table
        size="small"
        stickyHeader
      >
        {/* ================= HEADER ================= */}
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Visitor Name
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Department
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Company
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Purpose
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Location
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Visit Date
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Duration
            </TableCell>

            <TableCell
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Status
            </TableCell>

          </TableRow>
        </TableHead>

        {/* ================= BODY ================= */}
        <TableBody>

          {filteredDetails
            .slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            )
            .map((item, index) => (

              <TableRow
                key={item.id || `${page}-${index}`}
                hover
              >
                {/* Visitor */}
                <TableCell>
                  {item.visitorName || "-"}
                </TableCell>

                {/* Department */}
                <TableCell>
                  {item.department || "-"}
                </TableCell>

                {/* Company */}
                <TableCell>
                  {item.representing || "-"}
                </TableCell>

                {/* Purpose */}
                <TableCell
                  sx={{
                    maxWidth: 280,
                  }}
                >
                  <Tooltip
                    title={item.purpose || ""}
                  >
                    <Typography
                      fontSize={13}
                      noWrap
                      sx={{
                        maxWidth: 280,
                      }}
                    >
                      {item.purpose || "-"}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Location */}
                <TableCell>
                  {item.location || "-"}
                </TableCell>

                {/* Visit Date */}
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.visitingDate
                    ? formatDate(item.visitingDate)
                    : "-"}
                </TableCell>

                {/* Duration */}
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.visitDurationMinutes != null
                    ? `${item.visitDurationMinutes} min`
                    : "-"}
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      item.finalApprovalStatus ||
                      "Unknown"
                    }
                    color={getStatusColor(
                      item.finalApprovalStatus
                    )}
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  />
                </TableCell>

              </TableRow>
            ))}

          {/* ================= NO DATA ================= */}
          {filteredDetails.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={9}
                align="center"
              >
                <Typography
                  color="text.secondary"
                  py={4}
                >
                  No visitor records found
                </Typography>
              </TableCell>
            </TableRow>
          )}

        </TableBody>
      </Table>
    </TableContainer>

    {/* =====================================================
        PAGINATION
    ===================================================== */}
    <TablePagination
      component="div"
      count={filteredDetails.length}
      page={page}
      onPageChange={(event, newPage) => {
        setPage(newPage);
      }}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(
          parseInt(event.target.value, 10)
        );
        setPage(0);
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage="Rows per page:"
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        mt: 1,
      }}
    />

  </CardContent>
</Card>
    </Box>
  );
};

export default VisitorDashboard;